import { Button } from '@consigli/facade';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineShieldCheck, HiOutlineSwitchHorizontal } from 'react-icons/hi';

import { BidVsBidControls } from './bid-vs-bid-controls';
import { RiskInsightGapsControls } from './risk-insight-gaps-controls';
import { ServiceOptionCard } from './service-option-card';
import { BidReviewService, CompareConfig } from './types';

interface CompareStepProps {
  config: CompareConfig;
  onConfigChange: (config: CompareConfig) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitEnabled: boolean;
}

export const CompareStep: FC<CompareStepProps> = ({
  config,
  onConfigChange,
  onBack,
  onSubmit,
  isSubmitEnabled,
}) => {
  const { t } = useTranslation();

  const toggleService = (service: BidReviewService) => {
    const isSelected = config.services.includes(service);
    onConfigChange({
      ...config,
      services: isSelected
        ? config.services.filter((current) => current !== service)
        : [...config.services, service],
    });
  };

  const riskSelected = config.services.includes(BidReviewService.RISK_INSIGHT_GAPS);
  const bidVsBidSelected = config.services.includes(BidReviewService.BID_VS_BID);

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="w-full rounded-lg border border-day-light-2 bg-day-light-5 p-6">
        <h3 className="text-lg font-semibold text-day-neutral-dark">
          {t('bid-review.compare.services-to-run')}
        </h3>
        <p className="mb-4 text-sm text-day-neutral-subtle">
          {t('bid-review.compare.select-one-or-more')}
        </p>
        <div className="flex flex-col gap-4 sm:flex-row">
          <ServiceOptionCard
            icon={<HiOutlineShieldCheck size={20} />}
            title={t('bid-review.compare.risk-insight-gaps-title')}
            description={t('bid-review.compare.risk-insight-gaps-description')}
            selected={riskSelected}
            onToggle={() => toggleService(BidReviewService.RISK_INSIGHT_GAPS)}
          />
          <ServiceOptionCard
            icon={<HiOutlineSwitchHorizontal size={20} />}
            title={t('bid-review.compare.bid-vs-bid-title')}
            description={t('bid-review.compare.bid-vs-bid-description')}
            selected={bidVsBidSelected}
            onToggle={() => toggleService(BidReviewService.BID_VS_BID)}
          />
        </div>
      </div>

      {riskSelected && <RiskInsightGapsControls config={config} onChange={onConfigChange} />}

      {bidVsBidSelected && <BidVsBidControls config={config} onChange={onConfigChange} />}

      <div className="mt-2 flex items-center justify-between gap-4">
        <Button secondary rounded onClick={onBack} className="px-8 text-sm">
          {t('package-creation.back')}
        </Button>
        <Button
          primary
          rounded
          onClick={onSubmit}
          disabled={!isSubmitEnabled}
          className="px-8 text-sm"
        >
          {t('bid-review.compare.compare-now')}
        </Button>
      </div>
    </div>
  );
};
